/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { IVideoPlayerAdditionalContentViewProps } from '@msdyn365-commerce-modules/video-player/src/modules/video-player/./components/additional-content';
import { IVideoPlayerViewProps } from '@msdyn365-commerce-modules/video-player/src/modules/video-player/./video-player';
import { IVideoPlayerConfig, IVideoPlayerProps } from '../definition-extensions/video-player.ext.props.autogenerated';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IVideoPlayerAdditionalContentViewProps) => {
    if (additionalContent.additionalContentItems) {
        return (
            <Node {...additionalContent.additionalContentNode}>
                <>
                    {additionalContent.additionalContentItems.heading}
                    <Node {...additionalContent.additionalContentItems.additionalContentItemContainer}>
                        {additionalContent.additionalContentItems.text}
                        <Node {...additionalContent.additionalContentItems.additionalContentItemLinks}>
                            {additionalContent.additionalContentItems.links}
                        </Node>
                    </Node>
                </>
            </Node>
        );
    }

    return null;
};

const renderScrollButtons = (configs: IVideoPlayerConfig): JSX.Element | undefined => {
    return (
        configs.scrollToItems && (
            <Node className='ms-video-player-scroll-button'>
                {configs.scrollToItems?.map(item => (
                    <button
                        className={item?.buttonClass}
                        aria-label={item?.buttonAriaLabel}
                        onClick={() => scrollToSection(item.buttonId, item.scrollOffsetPosition)}
                    >
                        {item?.buttonText}
                    </button>
                ))}
            </Node>
        )
    );
};

const scrollToSection = (id: string, position: string | undefined) => {
    if (MsDyn365.isBrowser) {
        const offsetPosition = position ?? 'center';
        document?.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: offsetPosition as ScrollLogicalPosition });
    }
};

const VideoPlayerView: React.FC<IVideoPlayerViewProps & IVideoPlayerProps<{}>> = props => {
    return (
        <div id={props.id} className='ms-video-player' {...props.renderModuleAttributes(props)}>
            {props.player}
            <Node {...props.detailsContainer} className={props.detailsContainer ? props.detailsContainer.className : ''}>
                {props.title}
                {props.text}
                {props.additionalContent && renderAdditionalContent(props.additionalContent)}
                <Node {...props.detailsContainerCta} className={props.detailsContainerCta ? props.detailsContainerCta.className : ''}>
                    {props.links}
                    {renderScrollButtons(props.config)}
                </Node>
            </Node>
        </div>
    );
};

export default VideoPlayerView;
